import { defineStore } from "pinia";
import { ref } from "vue";
import * as convApi from "@/api/conversations";
import * as docApi from "@/api/documents";
import type {
  ConversationDetail,
  ConversationOut,
  DocumentOut,
  DocumentSummary,
  MessageOut,
} from "@/types/api";

export const useConversationStore = defineStore("conversation", () => {
  const list = ref<ConversationOut[]>([]);
  const activeId = ref<number | null>(null);
  const messages = ref<MessageOut[]>([]);
  const detail = ref<ConversationDetail | null>(null);
  const documents = ref<DocumentSummary[]>([]);
  const activeDocument = ref<DocumentOut | null>(null);
  const loadingList = ref(false);
  const loadingDetail = ref(false);

  async function loadList() {
    loadingList.value = true;
    try {
      const r = await convApi.listConversations({ page_size: 50 });
      list.value = r.items;
    } finally {
      loadingList.value = false;
    }
  }

  async function selectConversation(id: number) {
    activeId.value = id;
    loadingDetail.value = true;
    try {
      const [d, docs] = await Promise.all([
        convApi.getConversation(id),
        docApi.listConversationDocuments(id),
      ]);
      detail.value = d;
      messages.value = d.messages;
      documents.value = docs;
      activeDocument.value = d.latest_prd_id
        ? await docApi.getDocument(d.latest_prd_id)
        : null;
    } finally {
      loadingDetail.value = false;
    }
  }

  async function startNew(title: string) {
    const conv = await convApi.createConversation(title);
    list.value = [conv, ...list.value];
    await selectConversation(conv.id);
  }

  async function refreshActive() {
    if (activeId.value !== null) {
      await selectConversation(activeId.value);
    }
  }

  function appendMessageLocally(msg: MessageOut) {
    messages.value = [...messages.value, msg];
  }

  function updateLastAssistant(textDelta: string) {
    const last = messages.value[messages.value.length - 1];
    if (last && last.role === "assistant") {
      last.content += textDelta;
    }
  }

  function setLastAssistant(text: string) {
    const last = messages.value[messages.value.length - 1];
    if (last && last.role === "assistant") {
      last.content = text;
    }
  }

  async function confirmPRD() {
    if (!detail.value) return;
    await convApi.confirmPRD(detail.value.id);
    await refreshActive();
  }

  return {
    list, activeId, messages, detail, documents, activeDocument,
    loadingList, loadingDetail,
    loadList, selectConversation, startNew, refreshActive,
    appendMessageLocally, updateLastAssistant, setLastAssistant, confirmPRD,
  };
});
